import React, { FormEvent, useState } from 'react';
import { Card } from 'react-bootstrap';
import { CreditCard } from 'lucide-react';

interface EditPaymentMethodFormProps {
    paymentMethod: {
        id: string;
        card: {
            brand: string;
            last4: string;
            exp_month: number;
            exp_year: number;
        };
        billing_details?: {
            name?: string;
            address?: {
                line1?: string;
                line2?: string;
                city?: string;
                state?: string;
                postal_code?: string;
                country?: string;
            };
        };
    };
    onUpdate: (paymentMethodId: string, billingDetails: {
        name: string;
        address: {
            line1: string;
            line2?: string;
            city: string;
            state: string;
            postal_code: string;
            country: string;
        };
    }) => Promise<void>;
    className?: string;
}

const EditPaymentMethodForm: React.FC<EditPaymentMethodFormProps> = ({
                                                                         paymentMethod,
                                                                         onUpdate,
                                                                         className = ''
                                                                     }) => {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [billingDetails, setBillingDetails] = useState({
        name: paymentMethod.billing_details?.name || '',
        address: {
            line1: paymentMethod.billing_details?.address?.line1 || '',
            line2: paymentMethod.billing_details?.address?.line2 || '',
            city: paymentMethod.billing_details?.address?.city || '',
            state: paymentMethod.billing_details?.address?.state || '',
            postal_code: paymentMethod.billing_details?.address?.postal_code || '',
            country: paymentMethod.billing_details?.address?.country || 'US'
        }
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name.startsWith('address.')) {
            const field = name.split('.')[1];
            setBillingDetails(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [field]: value
                }
            }));
        } else {
            setBillingDetails(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await onUpdate(paymentMethod.id, billingDetails);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className={`border-0 shadow-sm ${className}`}>
            <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                    <CreditCard size={24} className="text-primary me-2" />
                    <h6 className="mb-0">Edit Payment Method</h6>
                </div>

                <div className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                        <span className="text-capitalize me-2">{paymentMethod.card.brand}</span>
                        <span>•••• {paymentMethod.card.last4}</span>
                        <span className="ms-2 text-muted">
                            Expires {String(paymentMethod.card.exp_month).padStart(2, '0')}/{paymentMethod.card.exp_year}
                        </span>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label">Cardholder Name</label>
                        <input
                            type="text"
                            name="name"
                            className="form-control"
                            value={billingDetails.name}
                            onChange={handleInputChange}
                            placeholder="Name on card"
                            required
                        />
                    </div>

                    <h6 className="mb-3">Billing Address</h6>

                    <div className="mb-3">
                        <label className="form-label">Street Address</label>
                        <input
                            type="text"
                            name="address.line1"
                            className="form-control"
                            value={billingDetails.address.line1}
                            onChange={handleInputChange}
                            placeholder="Street address, P.O. box"
                            required
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Apt, Suite, Unit (Optional)</label>
                        <input
                            type="text"
                            name="address.line2"
                            className="form-control"
                            value={billingDetails.address.line2}
                            onChange={handleInputChange}
                            placeholder="Apartment, suite, unit, etc."
                        />
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label">City</label>
                            <input
                                type="text"
                                name="address.city"
                                className="form-control"
                                value={billingDetails.address.city}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">State / Province</label>
                            <input
                                type="text"
                                name="address.state"
                                className="form-control"
                                value={billingDetails.address.state}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label">ZIP / Postal Code</label>
                            <input
                                type="text"
                                name="address.postal_code"
                                className="form-control"
                                value={billingDetails.address.postal_code}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Country</label>
                            <select
                                name="address.country"
                                className="form-select"
                                value={billingDetails.address.country}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="US">United States</option>
                                <option value="CA">Canada</option>
                                <option value="GB">United Kingdom</option>
                                <option value="AU">Australia</option>
                                <option value="DE">Germany</option>
                                <option value="FR">France</option>
                                <option value="IT">Italy</option>
                                <option value="ES">Spain</option>
                                <option value="NL">Netherlands</option>
                                <option value="BE">Belgium</option>
                                <option value="AT">Austria</option>
                                <option value="CH">Switzerland</option>
                                <option value="SE">Sweden</option>
                                <option value="NO">Norway</option>
                                <option value="DK">Denmark</option>
                                <option value="FI">Finland</option>
                                <option value="IE">Ireland</option>
                                <option value="NZ">New Zealand</option>
                            </select>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger mb-4" role="alert">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </form>
            </Card.Body>
        </Card>
    );
};

export default EditPaymentMethodForm;